/**
 * Family registry — open by design.
 *
 * `family` on `ThemeIconographyConfig` is a free-form string: any
 * Iconify prefix (200+ collections, ~200,000 icons) is accepted at
 * runtime. The two pieces of family-specific behaviour that DO need
 * code live here:
 *
 *   1. `VARIANT_REWRITERS` — appends `-bold` / `-fill` / `-solid` etc
 *      when the active theme requests a weight or style and the
 *      family supports the axis. Unknown family → identity (no
 *      rewrite).
 *
 *   2. `LAZY_LOADERS` — families whose `@iconify-json/<prefix>` JSON
 *      catalog is not eager-installed. Triggered on first render with
 *      that family active. Iconify's collection cache deduplicates.
 *
 * Adding more sophistication for a family (e.g. tabler's `-filled`
 * suffix) is a single entry here — no per-icon mapping required.
 * Concept-to-FQN resolution is data-driven via `divergence.json`,
 * machine-generated by `scripts/build-icon-divergence.mjs`.
 */
import { addCollection, setCustomIconsLoader } from '@iconify/vue'

export interface VariantContext {
  weight?: string
  style?: string
}

export type VariantRewriter = (fqn: string, ctx: VariantContext) => string

const PHOSPHOR_WEIGHTS = new Set(['thin', 'light', 'regular', 'bold', 'fill', 'duotone'])
const HEROICONS_STYLES = new Set(['outline', 'solid'])
const SOLAR_STYLES = new Set(['linear', 'outline', 'bold', 'broken', 'duotone', 'bold-duotone'])

const identityRewriter: VariantRewriter = fqn => fqn

const phosphorRewriter: VariantRewriter = (fqn, { weight }) => {
  if (!weight || !PHOSPHOR_WEIGHTS.has(weight) || weight === 'regular') return fqn
  return `${fqn}-${weight}`
}

const heroiconsRewriter: VariantRewriter = (fqn, { style }) => {
  if (!style || !HEROICONS_STYLES.has(style) || style === 'outline') return fqn
  return `${fqn}-solid`
}

const solarRewriter: VariantRewriter = (fqn, { style }) => {
  if (!style || !SOLAR_STYLES.has(style) || style === 'linear') return fqn
  return `${fqn}-${style}`
}

/**
 * Per-family transformer. Unknown family → identity, so any of the
 * 200+ Iconify collections works without registration.
 */
export const VARIANT_REWRITERS: Record<string, VariantRewriter> = {
  ph: phosphorRewriter,
  phosphor: phosphorRewriter,
  heroicons: heroiconsRewriter,
  solar: solarRewriter,
}

export function rewriteVariant(fqn: string, family: string, ctx: VariantContext): string {
  const rewriter = VARIANT_REWRITERS[family] ?? identityRewriter
  return rewriter(fqn, ctx)
}

/**
 * Lazy loaders, keyed by family. Every family the Icon component may
 * render — including `DEFAULT_FAMILY` (lucide) — needs an entry here;
 * without one `ensureLazyLoaded()` silently no-ops and Iconify's `<Icon>`
 * falls through to its own runtime network fetch (api.iconify.design /
 * simplesvg.com / unisvg.com), which strict CSP `connect-src` blocks in
 * production. `lucide`/`mdi` were missing here despite their
 * `@iconify-json/*` packages being real `dependencies` (not devDeps) —
 * the registration line was dropped in the migration off the old
 * per-family static-map architecture (archive/icon-per-family-maps-*).
 * Confirmed via consuming apps (elveris-pass) observing exactly this
 * fetch-then-CSP-block failure for lucide icon names.
 */
export type IconLazyLoader = () => Promise<void>

type IconSet = Parameters<typeof addCollection>[0]

/**
 * Pick the IconifyJSON out of whatever shape the bundler hands back.
 *
 * The package entry (`@iconify-json/x`) exports the set as the named export
 * `icons`; a direct `.json` import exports it as `default` — AND, under Vite,
 * every top-level JSON key as a named export too, so `mod.icons` is then the
 * set's inner name→body MAP, not the set. The previous `mod.icons ?? mod.default`
 * picked that map first for every `/icons.json` loader below, `addCollection`
 * rejected it silently (no `prefix`), and the warm-up never loaded anything —
 * measured 2026-10-03 in the kit Storybook build: with the Iconify API blocked,
 * 0 of 111 catalog icons rendered. So each candidate is checked for the
 * IconifyJSON shape instead of trusting the export name.
 */
function asIconSet(mod: unknown): IconSet | null {
  const m = mod as Record<string, unknown> | null
  for (const c of [m?.default, m?.icons, m]) {
    const set = c as { prefix?: unknown, icons?: unknown } | null | undefined
    if (set && typeof set.prefix === 'string' && set.icons && typeof set.icons === 'object') return set as IconSet
  }
  return null
}

async function loadCollection(loaderModule: Promise<unknown>): Promise<void> {
  const set = asIconSet(await loaderModule)
  if (set) addCollection(set)
}

/**
 * THIS STAYS ON THE `/icons.json` PATH — unlike catalogs.ts. (2026-09-13)
 *
 * catalogs.ts moved to the package entry (`@iconify-json/lucide`) because it
 * has a Node consumer. This table runs only IN THE BROWSER (`addCollection`),
 * so Node's import-attribute requirement does not touch it — and converting
 * this one to the package entry too produced a MEASURED regression: the
 * elveris-avm build grew the `vendor-icons-buildtime-only` chunk to 31,6 MB
 * and blew up at vite-plugin-pwa's 2 MiB precache limit. That chunk name is a
 * deliberate tripwire in avm: "UnoCSS preset-icons reads these only at BUILD
 * time; if anything imports them at runtime, that is a bundling error" (avm
 * vite.config.ts). The `.json` form does not carry this weight into the
 * consumer's graph; the package entry (`index.mjs`) does.
 */
const COLLECTION_IMPORTS: Record<string, () => Promise<unknown>> = {
  lucide: () => import('@iconify-json/lucide/icons.json'),
  mdi: () => import('@iconify-json/mdi/icons.json'),
  heroicons: () => import('@iconify-json/heroicons/icons.json'),
  ph: () => import('@iconify-json/ph/icons.json'),
  tabler: () => import('@iconify-json/tabler/icons.json'),
  carbon: () => import('@iconify-json/carbon/icons.json'),
  fluent: () => import('@iconify-json/fluent/icons.json'),
  solar: () => import('@iconify-json/solar/icons.json'),
  iconoir: () => import('@iconify-json/iconoir/icons.json'),
}

export const LAZY_LOADERS: Record<string, IconLazyLoader> = {
  ...Object.fromEntries(Object.entries(COLLECTION_IMPORTS).map(([family, load]) => [family, () => loadCollection(load())])),
  phosphor: () => loadCollection(COLLECTION_IMPORTS.ph!()),
}

/**
 * NO NETWORK FOR A BUNDLED FAMILY (2026-10-03).
 *
 * `LAZY_LOADERS` above only WARMS Iconify's cache, asynchronously, for the
 * families the theme provides. Two paths still reached `api.iconify.design`:
 *   1. the race — `<IconifyIcon>` mounts and asks for its icon before the
 *      dynamic `import()` of the collection has resolved; Iconify sees a miss
 *      and queues an API request;
 *   2. literal `icon="mdi:fullscreen"` props (VideoPlayer, IconBrowser, …),
 *      whose prefix is never a provider family, so nothing warmed it at all.
 * Offline, or under a strict CSP `connect-src`, both render an empty box.
 *
 * Iconify's per-prefix custom loader is consulted INSTEAD of the API whenever
 * an icon of that prefix is missing (`loadNewIcons` in @iconify/core), so
 * registering one per bundled collection closes both paths: a miss is answered
 * from the same `@iconify-json/*` file the warm-up uses, never the network.
 * Prefixes the kit does not bundle are untouched (they have no local source).
 * Idempotent; called by Icon.vue before its first render.
 */
let localLoadersRegistered = false
export function registerLocalIconLoaders(): void {
  if (localLoadersRegistered) return
  localLoadersRegistered = true
  for (const [prefix, load] of Object.entries(COLLECTION_IMPORTS)) {
    setCustomIconsLoader(async () => asIconSet(await load()), prefix)
  }
}

/**
 * Suggested families surfaced in the ThemeIconPanel chooser. Not an
 * allowlist — consumers can pass any Iconify prefix via theme JSON.
 * Order roughly mirrors stylistic diversity for designer browsing.
 */
export const SUGGESTED_FAMILIES = [
  { prefix: 'lucide', name: 'Lucide', hint: 'Modern stroke (Linear/Vercel)' },
  { prefix: 'mdi', name: 'Material', hint: 'Material Design (enterprise)' },
  { prefix: 'tabler', name: 'Tabler', hint: 'Geometric stroke (Mantine)' },
  { prefix: 'ph', name: 'Phosphor', hint: 'Six weights (editorial)' },
  { prefix: 'heroicons', name: 'Heroicons', hint: 'Tailwind ecosystem' },
  { prefix: 'carbon', name: 'Carbon', hint: 'IBM rational' },
  { prefix: 'fluent', name: 'Fluent', hint: 'Microsoft soft' },
  { prefix: 'solar', name: 'Solar', hint: 'Multi-style modern' },
  { prefix: 'iconoir', name: 'Iconoir', hint: 'Thin editorial' },
] as const

export const DEFAULT_FAMILY = 'lucide'
